import './MatchCarousel.css'

import { useState, useRef, useEffect } from 'react'

import type { Match } from '../store'

import { MatchCard } from './MatchCard'
import { MatchSlot } from './MatchSlot'

export type MatchCarouselProps = {
  className?: string
  matches: Match[]
  emptySlots?: number
  resetAt?: string
  selectedMatchId: string | null
  onSelectMatch: (matchId: string) => void
}

export const MatchCarousel = (props: MatchCarouselProps) => {
  const { className, matches, emptySlots = 0, resetAt, selectedMatchId, onSelectMatch } = props

  const totalItems = matches.length + emptySlots

  const getInitialIndex = () => {
    if (selectedMatchId) {
      const matchIndex = matches.findIndex((m) => m.id === selectedMatchId)
      if (matchIndex !== -1) return matchIndex
    }
    return 0
  }

  const [focusedIndex, setFocusedIndex] = useState(getInitialIndex)
  const [isDragging, setIsDragging] = useState(false)
  const [dragStartX, setDragStartX] = useState(0)
  const [dragOffset, setDragOffset] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const trackRef = useRef<HTMLDivElement>(null)
  const justDraggedRef = useRef(false)
  const velocityRef = useRef({ x: 0, time: 0, lastX: 0 })
  const hasSetFromSelectedId = useRef(false)

  const getCardWidth = () => {
    if (!trackRef.current) return 200
    const card = trackRef.current.querySelector('.match-carousel__card') as HTMLElement
    if (!card) return 200
    const gap = parseFloat(getComputedStyle(trackRef.current).gap) || 0
    return card.offsetWidth + gap
  }

  const trackVelocity = (clientX: number) => {
    const now = Date.now()
    const dt = now - velocityRef.current.time
    if (dt > 0) {
      velocityRef.current = {
        x: (clientX - velocityRef.current.lastX) / dt,
        time: now,
        lastX: clientX,
      }
    }
  }

  const preventClickAfterDrag = () => {
    justDraggedRef.current = true
    setTimeout(() => {
      justDraggedRef.current = false
    }, 100)
  }

  const handleCardClick = (index: number, matchId: string | null) => {
    if (isDragging || justDraggedRef.current) return
    hasSetFromSelectedId.current = true
    if (index === focusedIndex && matchId) {
      onSelectMatch(matchId)
    } else {
      setFocusedIndex(index)
    }
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(false)
    setDragStartX(e.clientX)
    setDragOffset(0)
    velocityRef.current = { x: 0, time: Date.now(), lastX: e.clientX }
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (dragStartX === 0) return
    const diff = e.clientX - dragStartX
    if (Math.abs(diff) > 10) {
      setIsDragging(true)
      setDragOffset(diff)
      trackVelocity(e.clientX)
    }
  }

  const handleDragEnd = () => {
    if (isDragging && Math.abs(dragOffset) > 20) {
      // Calculate how many cards to move based on drag distance + velocity
      const cardWidth = getCardWidth()
      const velocity = velocityRef.current.x
      const velocityBoost = velocity * 80 // Convert velocity to extra pixels
      const totalOffset = dragOffset + velocityBoost
      const cardsToMove = Math.round(totalOffset / cardWidth) * -1
      const newIndex = Math.max(0, Math.min(totalItems - 1, focusedIndex + cardsToMove))

      hasSetFromSelectedId.current = true
      preventClickAfterDrag()

      // First disable dragging to re-enable transitions
      setIsDragging(false)
      setDragStartX(0)

      // Then update position with animation
      requestAnimationFrame(() => {
        setDragOffset(0)
        setFocusedIndex(newIndex)
      })
    } else {
      if (isDragging) preventClickAfterDrag()
      setIsDragging(false)
      setDragStartX(0)
      setDragOffset(0)
    }
  }

  const handleMouseUp = () => {
    handleDragEnd()
  }

  const handleMouseLeave = () => {
    if (dragStartX !== 0) {
      handleDragEnd()
    }
  }

  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0]
    if (!touch) return
    setIsDragging(false)
    setDragStartX(touch.clientX)
    setDragOffset(0)
    velocityRef.current = { x: 0, time: Date.now(), lastX: touch.clientX }
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    if (dragStartX === 0) return
    const touch = e.touches[0]
    if (!touch) return
    const diff = touch.clientX - dragStartX
    if (Math.abs(diff) > 10) {
      e.preventDefault()
      setIsDragging(true)
      setDragOffset(diff)
      trackVelocity(touch.clientX)
    }
  }

  const handleTouchEnd = () => {
    handleDragEnd()
  }

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        hasSetFromSelectedId.current = true
        setFocusedIndex((prev) => Math.max(0, prev - 1))
      } else if (e.key === 'ArrowRight') {
        hasSetFromSelectedId.current = true
        setFocusedIndex((prev) => Math.min(totalItems - 1, prev + 1))
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [totalItems])

  // Sync selectedMatchId prop to focused index (skip animation on first sync)
  useEffect(() => {
    if (!selectedMatchId) return

    const matchIndex = matches.findIndex((m) => m.id === selectedMatchId)
    if (matchIndex !== -1) {
      hasSetFromSelectedId.current = true
      setFocusedIndex(matchIndex)
    }
  }, [selectedMatchId, matches])

  // Calculate virtual index during drag for smooth visual feedback
  const cardWidth = getCardWidth()
  const virtualOffset = isDragging ? dragOffset / cardWidth : 0
  const virtualIndex = focusedIndex - virtualOffset

  // Determine which card should appear selected (clamped to valid range)
  const activeIndex = isDragging
    ? Math.max(0, Math.min(totalItems - 1, Math.round(virtualIndex)))
    : focusedIndex

  const getCardStyle = (index: number) => {
    const distance = index - virtualIndex
    const absDistance = Math.abs(distance)

    // Scale: focused = 1, others decrease
    const scale = Math.max(0.6, 1 - absDistance * 0.15)

    // Opacity: focused = 1, others fade
    const opacity = Math.max(0.3, 1 - absDistance * 0.25)

    // Z-index: focused card on top
    const zIndex = Math.round(10 - absDistance)

    // Translate cards at distance 2+ closer to center (smooth/continuous)
    let translateX = 0
    if (absDistance > 1) {
      const pullFactor = (absDistance - 1) * 20
      translateX = distance > 0 ? -pullFactor : pullFactor
    }

    return {
      transform: `scale(${scale}) translateX(${translateX}%)`,
      opacity,
      zIndex,
    }
  }

  const classNames = ['match-carousel', className].filter(Boolean).join(' ')
  const trackStyle = {
    '--focused-index': focusedIndex,
    '--drag-offset': `${dragOffset}px`,
  } as React.CSSProperties

  const trackClassNames = [
    'match-carousel__track',
    isDragging && 'match-carousel__track--dragging',
    !hasSetFromSelectedId.current && 'match-carousel__track--no-transition',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div
      className={classNames}
      ref={containerRef}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div className="match-carousel__void" />
      <div className="match-carousel__floor" />
      <div ref={trackRef} className={trackClassNames} style={trackStyle}>
        {matches.map((match, index) => (
          <div
            key={match.id}
            className={`match-carousel__card ${index === activeIndex ? 'match-carousel__card--focused' : ''}`}
            style={getCardStyle(index)}
            onClick={() => handleCardClick(index, match.id)}
          >
            <div className="match-carousel__card-inner">
              <MatchCard match={match} isSelected={index === activeIndex} isNewMatch />
            </div>
          </div>
        ))}
        {Array.from({ length: emptySlots }).map((_, slotIndex) => {
          const index = matches.length + slotIndex
          return (
            <div
              key={`slot-${slotIndex}`}
              className={`match-carousel__card ${index === activeIndex ? 'match-carousel__card--focused' : ''}`}
              style={getCardStyle(index)}
              onClick={() => handleCardClick(index, null)}
            >
              <div className="match-carousel__card-inner">
                <MatchSlot {...(resetAt !== undefined && { resetAt })} />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
